import { Navigate, createBrowserRouter } from "react-router-dom";
import { RouteHydrateFallback } from "@/components/shared/RouteHydrateFallback";
import { RouteErrorBoundary } from "@/router/RouteErrorBoundary";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ChangePasswordPage } from "@/pages/auth/ChangePasswordPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { ResetPasswordPage } from "@/modules/auth/ui/ResetPasswordPage";
import { RequireRole } from "@/router/RequireRole";
import { RequirePasswordChange } from "@/router/RequirePasswordChange";
import { RedirectIfAuthenticated } from "@/router/RedirectIfAuthenticated";
import { roleDashboardPath } from "@/router/roleDashboardPath";
import { useAuthStore } from "@/store/auth.store";

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.role);
  if (!isAuthenticated) return <Navigate to="/parent/catalog" replace />;
  return <Navigate to={roleDashboardPath(role)} replace />;
}

export const router = createBrowserRouter([
  {
    id: "root",
    HydrateFallback: RouteHydrateFallback,
    // Monté sur la racine : couvre toutes les routes filles, y compris les écrans de classe en
    // direct, qui sont déclarés hors des layouts de rôle et n'ont donc aucune autre protection.
    errorElement: <RouteErrorBoundary />,
    children: [
      // ── Public ────────────────────────────────────────────────────────────────
      { path: "/", element: <RootRedirect /> },
      {
        element: <RedirectIfAuthenticated />,
        children: [{ path: "/login", element: <LoginPage /> }],
      },
      {
        path: "/impersonate-init",
        lazy: async () => {
          const { ImpersonateInitPage } = await import("@/pages/shared/ImpersonateInitPage");
          return { Component: ImpersonateInitPage };
        },
      },
      // Résolveur de clic sur une notification push web (cf. `public/firebase-messaging-sw.js` et
      // `docs/PWA_PUSH_NOTIFICATIONS.md` §5) — route vers la bonne page selon le rôle connecté.
      {
        path: "/n/:id",
        lazy: async () => {
          const { NotificationRedirectPage } =
            await import("@/pages/shared/NotificationRedirectPage");
          return { Component: NotificationRedirectPage };
        },
      },
      { path: "/change-password", element: <ChangePasswordPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/verify-email/:id/:hash", element: <VerifyEmailPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      {
        path: "/checkout/success",
        lazy: async () => {
          const { CheckoutSuccessPage } = await import("@/pages/parent/CheckoutSuccessPage");
          return { Component: CheckoutSuccessPage };
        },
      },
      {
        path: "/checkout/cancel",
        element: <Navigate to="/parent/checkout?step=payment" replace />,
      },

      // ── Password change guard (wraps all protected routes) ─────────────────
      {
        element: <RequirePasswordChange />,
        children: [
          // ── Public parent pages (accessible without auth) ────────────────────────
          {
            path: "/parent/dashboard",
            lazy: async () => {
              const { FamilyLayout } = await import("@/components/family/FamilyLayout");
              return { Component: FamilyLayout };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ParentDashboardPage } = await import("@/pages/parent/DashboardPage");
                  return { Component: ParentDashboardPage };
                },
              },
            ],
          },
          {
            path: "/parent/catalog",
            lazy: async () => {
              const { FamilyLayout } = await import("@/components/family/FamilyLayout");
              return { Component: FamilyLayout };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ParentCatalogPage } = await import("@/pages/parent/CatalogPage");
                  return { Component: ParentCatalogPage };
                },
              },
              {
                path: ":type",
                lazy: async () => {
                  const { ParentCatalogPage } = await import("@/pages/parent/CatalogPage");
                  return { Component: ParentCatalogPage };
                },
              },
            ],
          },

          // ── Public parent: tunnel d'achat mono-route (accessible aux invités) ─────
          // Une seule route gère panier → association → paiement via `?step=`.
          {
            path: "/parent/checkout",
            lazy: async () => {
              const { FamilyLayout } = await import("@/components/family/FamilyLayout");
              return { Component: FamilyLayout };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ParentCheckoutFunnelPage } =
                    await import("@/pages/parent/CheckoutFunnelPage");
                  return { Component: ParentCheckoutFunnelPage };
                },
              },
              {
                path: "confirmation",
                lazy: async () => {
                  const { CheckoutSuccessPage } =
                    await import("@/pages/parent/CheckoutSuccessPage");
                  return { Component: CheckoutSuccessPage };
                },
              },
            ],
          },

          // ── Admin ──────────────────────────────────────────────────────────────────
          {
            element: <RequireRole roles={["admin"]} />,
            children: [
              {
                path: "/admin",
                lazy: async () => {
                  const { AdminLayout } = await import("@/pages/admin/Layout");
                  return { Component: AdminLayout };
                },
                children: [
                  { index: true, element: <Navigate to="/admin/dashboard" replace /> },
                  {
                    path: "dashboard",
                    lazy: async () => {
                      const { AdminDashboardPage } = await import("@/pages/admin/DashboardPage");
                      return { Component: AdminDashboardPage };
                    },
                  },
                  {
                    path: "inscriptions/create",
                    lazy: async () => {
                      const { EnrollmentCreatePage } =
                        await import("@/pages/admin/EnrollmentCreatePage");
                      return { Component: EnrollmentCreatePage };
                    },
                  },
                  {
                    path: "inscriptions",
                    lazy: async () => {
                      const { AdminInscriptionsPage } =
                        await import("@/pages/admin/InscriptionsPage");
                      return { Component: AdminInscriptionsPage };
                    },
                  },
                  {
                    path: "students",
                    lazy: async () => {
                      const { StudentsPage } = await import("@/pages/admin/students/StudentsPage");
                      return { Component: StudentsPage };
                    },
                  },
                  {
                    path: "students/:id",
                    lazy: async () => {
                      const { StudentsPage } = await import("@/pages/admin/students/StudentsPage");
                      return { Component: StudentsPage };
                    },
                  },
                  {
                    path: "parents",
                    lazy: async () => {
                      const { AdminParentsPage } = await import("@/pages/admin/ParentsPage");
                      return { Component: AdminParentsPage };
                    },
                  },
                  {
                    path: "parents/:id",
                    lazy: async () => {
                      const { AdminParentsPage } = await import("@/pages/admin/ParentsPage");
                      return { Component: AdminParentsPage };
                    },
                  },
                  {
                    path: "teachers",
                    lazy: async () => {
                      const { AdminTeachersPage } = await import("@/pages/admin/TeachersPage");
                      return { Component: AdminTeachersPage };
                    },
                  },
                  {
                    path: "teachers/:id",
                    lazy: async () => {
                      const { AdminTeachersPage } = await import("@/pages/admin/TeachersPage");
                      return { Component: AdminTeachersPage };
                    },
                  },
                  {
                    path: "classes",
                    lazy: async () => {
                      const { AdminClassesPage } = await import("@/pages/admin/ClassesPage");
                      return { Component: AdminClassesPage };
                    },
                  },
                  {
                    path: "classes/:id",
                    lazy: async () => {
                      const { AdminClassesPage } = await import("@/pages/admin/ClassesPage");
                      return { Component: AdminClassesPage };
                    },
                  },
                  {
                    path: "planning",
                    lazy: async () => {
                      const { AdminPlanningPage } = await import("@/pages/admin/PlanningPage");
                      return { Component: AdminPlanningPage };
                    },
                  },
                  {
                    path: "payments",
                    lazy: async () => {
                      const { AdminPaymentsPage } = await import("@/pages/admin/PaymentsPage");
                      return { Component: AdminPaymentsPage };
                    },
                  },
                  {
                    path: "invoices",
                    lazy: async () => {
                      const { AdminInvoicesPage } = await import("@/pages/admin/InvoicesPage");
                      return { Component: AdminInvoicesPage };
                    },
                  },
                  {
                    path: "elearning",
                    lazy: async () => {
                      const { AdminELearningPage } = await import("@/pages/admin/ELearningPage");
                      return { Component: AdminELearningPage };
                    },
                  },
                  {
                    path: "leaderboard",
                    lazy: async () => {
                      const { LeaderboardPage } = await import("@/pages/admin/LeaderboardPage");
                      return { Component: LeaderboardPage };
                    },
                  },
                  {
                    path: "offers",
                    lazy: async () => {
                      const { AdminOffersPage } = await import("@/pages/admin/OffersPage");
                      return { Component: AdminOffersPage };
                    },
                  },
                  {
                    path: "messaging",
                    lazy: async () => {
                      const { AdminMessagingPage } = await import("@/pages/admin/MessagingPage");
                      return { Component: AdminMessagingPage };
                    },
                  },
                  {
                    path: "messaging/:id",
                    lazy: async () => {
                      const { AdminMessagingPage } = await import("@/pages/admin/MessagingPage");
                      return { Component: AdminMessagingPage };
                    },
                  },
                  {
                    path: "announcements",
                    lazy: async () => {
                      const { AdminAnnouncementsPage } =
                        await import("@/pages/admin/AnnouncementsPage");
                      return { Component: AdminAnnouncementsPage };
                    },
                  },
                  {
                    path: "notifications",
                    lazy: async () => {
                      const { AdminNotificationsPage } =
                        await import("@/pages/admin/NotificationsPage");
                      return { Component: AdminNotificationsPage };
                    },
                  },
                  {
                    path: "critical-alerts",
                    lazy: async () => {
                      const { CriticalAlertsPage } =
                        await import("@/pages/admin/CriticalAlertsPage");
                      return { Component: CriticalAlertsPage };
                    },
                  },
                  {
                    path: "users",
                    lazy: async () => {
                      const { UtilisateursPage } = await import("@/pages/admin/UtilisateursPage");
                      return { Component: UtilisateursPage };
                    },
                  },
                  {
                    path: "settings",
                    lazy: async () => {
                      const { AdminSettingsPage } = await import("@/pages/admin/SettingsPage");
                      return { Component: AdminSettingsPage };
                    },
                  },
                  {
                    path: "settings/academic",
                    lazy: async () => {
                      const { AdminAcademicSettingsPage } =
                        await import("@/pages/admin/AcademicSettingsPage");
                      return { Component: AdminAcademicSettingsPage };
                    },
                  },
                ],
              },
              {
                path: "/admin/live-class/:id",
                lazy: async () => {
                  const { AdminLiveClassPage } = await import("@/pages/admin/LiveClassPage");
                  return { Component: AdminLiveClassPage };
                },
              },
            ],
          },

          // ── Teacher ────────────────────────────────────────────────────────────────
          {
            element: <RequireRole roles={["teacher"]} />,
            children: [
              {
                path: "/teacher",
                lazy: async () => {
                  const { TeacherLayout } = await import("@/pages/teacher/Layout");
                  return { Component: TeacherLayout };
                },
                children: [
                  { index: true, element: <Navigate to="/teacher/dashboard" replace /> },
                  {
                    path: "dashboard",
                    lazy: async () => {
                      const { TeacherDashboardPage } =
                        await import("@/pages/teacher/DashboardPage");
                      return { Component: TeacherDashboardPage };
                    },
                  },
                  {
                    path: "classes",
                    lazy: async () => {
                      const { TeacherClassesPage } =
                        await import("@/pages/teacher/classes/TeacherClassesPage");
                      return { Component: TeacherClassesPage };
                    },
                  },
                  {
                    path: "classes/:uuid",
                    lazy: async () => {
                      const { TeacherClassDetailPage } =
                        await import("@/pages/teacher/classes/TeacherClassDetailPage");
                      return { Component: TeacherClassDetailPage };
                    },
                  },
                  {
                    path: "schedule",
                    lazy: async () => {
                      const { TeacherSchedulePage } = await import("@/pages/teacher/SchedulePage");
                      return { Component: TeacherSchedulePage };
                    },
                  },
                  {
                    path: "homework",
                    lazy: async () => {
                      const { TeacherHomeworkPage } = await import("@/pages/teacher/HomeworkPage");
                      return { Component: TeacherHomeworkPage };
                    },
                  },
                  // {
                  //   path: "elearning",
                  //   lazy: async () => {
                  //     const { TeacherELearningPage } = await import("@/pages/teacher/ELearningPage");
                  //     return { Component: TeacherELearningPage };
                  //   },
                  // },
                  {
                    path: "resources",
                    lazy: async () => {
                      const { TeacherResourcesPage } =
                        await import("@/pages/teacher/ResourcesPage");
                      return { Component: TeacherResourcesPage };
                    },
                  },
                  {
                    path: "messaging",
                    lazy: async () => {
                      const { TeacherMessagingPage } =
                        await import("@/pages/teacher/MessagingPage");
                      return { Component: TeacherMessagingPage };
                    },
                  },
                  {
                    path: "notifications",
                    lazy: async () => {
                      const { TeacherNotificationsPage } =
                        await import("@/pages/teacher/NotificationsPage");
                      return { Component: TeacherNotificationsPage };
                    },
                  },
                  {
                    path: "settings",
                    lazy: async () => {
                      const { TeacherSettingsPage } = await import("@/pages/teacher/SettingsPage");
                      return { Component: TeacherSettingsPage };
                    },
                  },
                ],
              },
              {
                path: "/teacher/live-class/:uuid",
                lazy: async () => {
                  const { TeacherLiveClassPage } = await import("@/pages/teacher/LiveClassPage");
                  return { Component: TeacherLiveClassPage };
                },
              },
            ],
          },

          // ── Parent ─────────────────────────────────────────────────────────────────
          {
            element: <RequireRole roles={["family"]} />,
            children: [
              {
                path: "/parent",
                lazy: async () => {
                  const { FamilyLayout } = await import("@/pages/parent/Layout");
                  return { Component: FamilyLayout };
                },
                children: [
                  { index: true, element: <Navigate to="/parent/dashboard" replace /> },
                  {
                    path: "classes",
                    lazy: async () => {
                      const { ParentClassesPage } = await import("@/pages/parent/ClassesPage");
                      return { Component: ParentClassesPage };
                    },
                  },
                  {
                    path: "classes/:uuid",
                    lazy: async () => {
                      const { ParentClassDetailPage } =
                        await import("@/pages/parent/ParentClassDetailPage");
                      return { Component: ParentClassDetailPage };
                    },
                  },
                  {
                    path: "invoices",
                    lazy: async () => {
                      const { ParentInvoicesPage } = await import("@/pages/parent/InvoicesPage");
                      return { Component: ParentInvoicesPage };
                    },
                  },
                  {
                    path: "payments/success",
                    lazy: async () => {
                      const { PaymentSuccessPage } =
                        await import("@/pages/parent/PaymentSuccessPage");
                      return { Component: PaymentSuccessPage };
                    },
                  },
                  {
                    path: "payments/cancel",
                    lazy: async () => {
                      const { PaymentCancelPage } =
                        await import("@/pages/parent/PaymentCancelPage");
                      return { Component: PaymentCancelPage };
                    },
                  },
                  {
                    path: "messaging",
                    lazy: async () => {
                      const { ParentMessagingPage } = await import("@/pages/parent/MessagingPage");
                      return { Component: ParentMessagingPage };
                    },
                  },
                  {
                    path: "notifications",
                    lazy: async () => {
                      const { ParentNotificationsPage } =
                        await import("@/pages/parent/NotificationsPage");
                      return { Component: ParentNotificationsPage };
                    },
                  },
                  {
                    path: "settings",
                    lazy: async () => {
                      const { ParentAccountPage } = await import("@/pages/parent/AccountPage");
                      return { Component: ParentAccountPage };
                    },
                  },
                  {
                    path: "student/:id",
                    lazy: async () => {
                      const { ParentStudentDashboardPage } =
                        await import("@/pages/parent/StudentDashboardPage");
                      return { Component: ParentStudentDashboardPage };
                    },
                  },
                ],
              },
            ],
          },

          // ── Student ────────────────────────────────────────────────────────────────
          {
            element: <RequireRole roles={["student"]} />,
            children: [
              {
                path: "/student",
                lazy: async () => {
                  const { StudentLayout } = await import("@/pages/student/Layout");
                  return { Component: StudentLayout };
                },
                children: [
                  { index: true, element: <Navigate to="/student/dashboard" replace /> },
                  {
                    path: "dashboard",
                    lazy: async () => {
                      const { StudentDashboardPage } =
                        await import("@/pages/student/DashboardPage");
                      return { Component: StudentDashboardPage };
                    },
                  },
                  {
                    path: "classes",
                    lazy: async () => {
                      const { StudentClassesPage } = await import("@/pages/student/ClassesPage");
                      return { Component: StudentClassesPage };
                    },
                  },
                  {
                    path: "classes/:uuid",
                    lazy: async () => {
                      const { StudentClassDetailPage } =
                        await import("@/pages/student/StudentClassDetailPage");
                      return { Component: StudentClassDetailPage };
                    },
                  },
                  // {
                  //   path: "schedule",
                  //   lazy: async () => {
                  //     const { StudentSchedulePage } = await import("@/pages/student/SchedulePage");
                  //     return { Component: StudentSchedulePage };
                  //   },
                  // },
                  {
                    path: "gamification",
                    lazy: async () => {
                      const { StudentGamificationPage } =
                        await import("@/pages/student/GamificationPage");
                      return { Component: StudentGamificationPage };
                    },
                  },
                  {
                    path: "homework",
                    lazy: async () => {
                      const { StudentHomeworkPage } = await import("@/pages/student/HomeworkPage");
                      return { Component: StudentHomeworkPage };
                    },
                  },
                  {
                    path: "elearning",
                    lazy: async () => {
                      const { StudentELearningPage } =
                        await import("@/pages/student/ELearningPage");
                      return { Component: StudentELearningPage };
                    },
                  },
                  {
                    path: "elearning/:id",
                    lazy: async () => {
                      const { StudentLessonPlayerPage } =
                        await import("@/pages/student/LessonPlayerPage");
                      return { Component: StudentLessonPlayerPage };
                    },
                  },
                  {
                    path: "resources",
                    lazy: async () => {
                      const { StudentResourcesPage } =
                        await import("@/pages/student/ResourcesPage");
                      return { Component: StudentResourcesPage };
                    },
                  },
                  {
                    path: "messaging",
                    lazy: async () => {
                      const { StudentMessagingPage } =
                        await import("@/pages/student/MessagingPage");
                      return { Component: StudentMessagingPage };
                    },
                  },
                  {
                    path: "notifications",
                    lazy: async () => {
                      const { StudentNotificationsPage } =
                        await import("@/pages/student/NotificationsPage");
                      return { Component: StudentNotificationsPage };
                    },
                  },
                  // {
                  //   path: "invoices",
                  //   lazy: async () => {
                  //     const { StudentInvoicesPage } = await import("@/pages/student/InvoicesPage");
                  //     return { Component: StudentInvoicesPage };
                  //   },
                  // },
                  {
                    path: "settings",
                    lazy: async () => {
                      const { StudentSettingsPage } = await import("@/pages/student/SettingsPage");
                      return { Component: StudentSettingsPage };
                    },
                  },
                ],
              },
              {
                path: "/student/live-class/:id",
                lazy: async () => {
                  const { StudentLiveClassPage } = await import("@/pages/student/LiveClassPage");
                  return { Component: StudentLiveClassPage };
                },
              },
            ],
          },
        ], // end RequirePasswordChange children
      },

      // ── Fallback ───────────────────────────────────────────────────────────────
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
